// AppHeader – top bar with user info, theme toggle, and logout.

import React, { useEffect, useState } from 'react';
import { User, Shield, Layers, Package, LogOut, ScanBarcode } from 'lucide-react';
import { ThemeToggle } from '../shared/ui/ThemeToggle';
import { RaceRangerIconMark } from '../branding/RaceRangerBrand';
import { getBarcodeScannerPort } from '../shared/barcodeScanner';

type AppHeaderProps = {
  userName: string;
  userEmail?: string;
  role: string;
  onLogout: () => void;
};

const PILL_BASE =
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium uppercase tracking-wider [&>svg]:size-3.5 [&>svg]:shrink-0';

const USER_PILL =
  'inline-flex items-center gap-1.5 max-w-[min(100%,14rem)] rounded-full border px-2.5 py-1 text-[11px] font-medium'
  + ' border-gray-200 bg-gray-100 text-gray-700 [&>svg]:text-blue-600'
  + ' dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:[&>svg]:text-blue-400';

const ROLE_PILL =
  `${PILL_BASE} border-blue-200 bg-blue-50 text-blue-800 [&>svg]:text-blue-600 dark:border-blue-800 dark:bg-blue-950/60 dark:text-blue-200 dark:[&>svg]:text-blue-400`;
const STAGE_PILL =
  `${PILL_BASE} border-amber-200 bg-amber-50 text-amber-800 [&>svg]:text-amber-600 dark:border-amber-800 dark:bg-amber-950/60 dark:text-amber-200 dark:[&>svg]:text-amber-400`;
const VER_PILL =
  `${PILL_BASE} border-emerald-200 bg-emerald-50 text-emerald-800 [&>svg]:text-emerald-600 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200 dark:[&>svg]:text-emerald-400`;

export default function AppHeader({ userName, userEmail, role, onLogout }: AppHeaderProps) {
  const displayName = (userName ?? '').trim() || '—';
  const userTooltip =
    userEmail && userEmail.trim() && userEmail.trim() !== displayName
      ? `${displayName} · ${userEmail.trim()}`
      : userEmail?.trim() || displayName;

  const appStage = (window.fmPublicEnv?.APP_STAGE ?? '').trim() || '—';
  const appVersion = (window.fmPublicEnv?.APP_VERSION ?? '').trim() || '—';

  // Web-only: Electron has direct OS serial access and never needs this. In the
  // browser, pairing a barcode scanner requires one explicit user click (the
  // Web Serial API rejects requestPort() calls made outside a user gesture) —
  // after that, useBarcodeScanner's own polling picks up the granted port
  // automatically wherever it's used, so this button doesn't need to know
  // whether a scanner is currently connected.
  const [connectingScanner, setConnectingScanner] = useState(false);
  const [scannerPaired, setScannerPaired] = useState(false);
  const canPairScanner = typeof window.serialAPI?.requestScannerPort === 'function';

  // Read-only poll (getPorts()/listSerialPorts(), never open/close) so this can safely
  // run everywhere AppHeader is mounted without joining useBarcodeScanner's single-owner
  // protocol for the shared physical port — reflects "paired" (permission granted),
  // not necessarily "another field currently has it open," since only one
  // useBarcodeScanner instance may hold the port at a time.
  useEffect(() => {
    if (!canPairScanner) return;
    let alive = true;
    const check = async (): Promise<void> => {
      const port = await getBarcodeScannerPort();
      if (alive) setScannerPaired(!!port);
    };
    void check();
    const timer = window.setInterval(() => void check(), 2000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [canPairScanner]);

  async function handleConnectScanner(): Promise<void> {
    if (!window.serialAPI?.requestScannerPort) return;
    setConnectingScanner(true);
    try {
      const res = await window.serialAPI.requestScannerPort();
      if (res.success) {
        window.dispatchEvent(
          new CustomEvent('toast-info', { detail: 'Scanner paired — it will connect automatically.' }),
        );
      } else {
        window.dispatchEvent(
          new CustomEvent('toast-error', { detail: String(res.message || 'Could not pair scanner') }),
        );
      }
    } finally {
      setConnectingScanner(false);
    }
  }

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 flex h-[56px] items-center justify-between gap-3 border-b px-4 sm:px-6"
      style={{
        background: 'var(--chrome-bg)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderColor: 'var(--chrome-border)',
      }}
      aria-label="RaceRanger Fleet Manager"
    >
      <span className="sr-only">RaceRanger Fleet Manager</span>
      <div className="flex shrink-0 items-center gap-2.5 min-w-0">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-slate-600 dark:bg-slate-900">
          <RaceRangerIconMark className="h-7 w-7" />
        </div>
        <div className="flex min-w-0 flex-col justify-center gap-0 leading-tight sm:flex-row sm:items-baseline sm:gap-2">
          <span className="text-sm font-semibold tracking-tight">
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              RaceRanger
            </span>
            <span className="ml-1.5 font-normal text-gray-500 dark:text-slate-400">Fleet Manager</span>
          </span>
        </div>
      </div>

      <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:gap-3">
        <div className="flex min-w-0 max-w-full items-center gap-2 overflow-x-auto overflow-y-hidden text-sm sm:gap-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex shrink-0 items-center gap-2" aria-label={`Logged in as ${displayName}`}>
            <span className={USER_PILL} title={userTooltip}>
              <User aria-hidden />
              <span className="truncate">{displayName.toUpperCase()}</span>
            </span>
            <span className={ROLE_PILL} title={`Role: ${role}`}>
              <Shield aria-hidden />
              {role}
            </span>
          </div>

          <span
            className="hidden sm:block h-6 w-px shrink-0 bg-gray-200 dark:bg-slate-600"
            role="separator"
            aria-orientation="vertical"
          />

          <div className="hidden sm:flex shrink-0 items-center gap-2">
            <span className={STAGE_PILL} title={`App stage: ${appStage}`}>
              <Layers aria-hidden />
              {appStage}
            </span>
            <span className={VER_PILL} title={`App version: ${appVersion}`}>
              <Package aria-hidden />
              {appVersion}
            </span>
          </div>
        </div>

        {canPairScanner ? (
          <button
            type="button"
            onClick={() => void handleConnectScanner()}
            disabled={connectingScanner}
            title={scannerPaired ? 'Barcode scanner paired' : 'Pair a USB barcode scanner with this browser tab'}
            aria-label={scannerPaired ? 'Barcode scanner paired' : 'Connect barcode scanner'}
            className={
              scannerPaired
                ? 'inline-flex shrink-0 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-emerald-600 transition-colors hover:bg-emerald-100 disabled:opacity-50 dark:border-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400 dark:hover:bg-emerald-900/60'
                : 'inline-flex shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100'
            }
          >
            <ScanBarcode className="h-4 w-4" aria-hidden />
          </button>
        ) : null}

        <ThemeToggle />

        <button
          type="button"
          onClick={onLogout}
          title="Log out"
          aria-label="Log out"
          className="inline-flex shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-2 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100"
        >
          <LogOut className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </header>
  );
}
